// Single source of truth for order status. Imported by both apps/api and
// apps/web so the status list, labels, and colors never drift apart.

export const ORDER_STATUSES = [
  'pending_payment',
  'paid',
  'assigned',
  'documents_requested',
  'documents_received',
  'verification_in_progress',
  'government_submission',
  'completed',
  'rejected',
  'refunded',
  'cancelled',
] as const;

export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  pending_payment: 'Pending Payment',
  paid: 'Paid',
  assigned: 'Assigned',
  documents_requested: 'Documents Requested',
  documents_received: 'Documents Received',
  verification_in_progress: 'Verification In Progress',
  government_submission: 'Government Submission',
  completed: 'Completed',
  rejected: 'Rejected',
  refunded: 'Refunded',
  cancelled: 'Cancelled',
};

// Tailwind color tokens per status, used by StatusBadge on both customer
// dashboard and admin panel so the same status always looks the same.
export const ORDER_STATUS_COLORS: Record<OrderStatus, { bg: string; text: string }> = {
  pending_payment: { bg: 'bg-amber-100', text: 'text-amber-800' },
  paid: { bg: 'bg-blue-100', text: 'text-blue-800' },
  assigned: { bg: 'bg-indigo-100', text: 'text-indigo-800' },
  documents_requested: { bg: 'bg-orange-100', text: 'text-orange-800' },
  documents_received: { bg: 'bg-cyan-100', text: 'text-cyan-800' },
  verification_in_progress: { bg: 'bg-purple-100', text: 'text-purple-800' },
  government_submission: { bg: 'bg-violet-100', text: 'text-violet-800' },
  completed: { bg: 'bg-green-100', text: 'text-green-800' },
  rejected: { bg: 'bg-red-100', text: 'text-red-800' },
  refunded: { bg: 'bg-slate-100', text: 'text-slate-800' },
  cancelled: { bg: 'bg-gray-100', text: 'text-gray-800' },
};

// Valid forward transitions for admin/executive status updates.
// Used to render only legal "next status" options in the admin UI,
// and re-validated server-side in orderService before any write.
export const ORDER_STATUS_TRANSITIONS: Record<OrderStatus, OrderStatus[]> = {
  pending_payment: ['paid', 'cancelled'],
  paid: ['assigned', 'refunded', 'cancelled'],
  assigned: ['documents_requested', 'verification_in_progress', 'cancelled'],
  documents_requested: ['documents_received', 'cancelled'],
  documents_received: ['verification_in_progress', 'documents_requested'],
  verification_in_progress: ['government_submission', 'documents_requested', 'rejected'],
  government_submission: ['completed', 'rejected'],
  completed: [],
  rejected: ['refunded'],
  refunded: [],
  cancelled: [],
};

// ---------------------------------------------------------------------
// CONSULTATION BOOKINGS — separate lifecycle from the main order flow.
// A consultation is a paid 30-minute call booked BEFORE a visitor
// commits to any service, so its statuses are deliberately small and
// don't share the order pipeline above (it never becomes an "order").
// ---------------------------------------------------------------------

export const CONSULTATION_STATUSES = [
  'pending_payment',
  'scheduled',
  'completed',
  'no_show',
  'cancelled',
  'refunded',
] as const;

export type ConsultationStatus = (typeof CONSULTATION_STATUSES)[number];

export const CONSULTATION_STATUS_LABELS: Record<ConsultationStatus, string> = {
  pending_payment: 'Pending Payment',
  scheduled: 'Scheduled',
  completed: 'Completed',
  no_show: 'No Show',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
};

export const CONSULTATION_STATUS_COLORS: Record<ConsultationStatus, { bg: string; text: string }> = {
  pending_payment: { bg: 'bg-amber-100', text: 'text-amber-800' },
  scheduled: { bg: 'bg-blue-100', text: 'text-blue-800' },
  completed: { bg: 'bg-green-100', text: 'text-green-800' },
  no_show: { bg: 'bg-orange-100', text: 'text-orange-800' },
  cancelled: { bg: 'bg-gray-100', text: 'text-gray-800' },
  refunded: { bg: 'bg-slate-100', text: 'text-slate-800' },
};

export const CONSULTATION_STATUS_TRANSITIONS: Record<ConsultationStatus, ConsultationStatus[]> = {
  pending_payment: ['scheduled', 'cancelled'],
  scheduled: ['completed', 'no_show', 'cancelled'],
  completed: [],
  no_show: ['scheduled', 'cancelled'],
  cancelled: ['refunded'],
  refunded: [],
};

// Fixed price for the pre-sales consultation call. Kept as a single
// exported constant (not a DB-configurable service_plan) since this is
// a flat-rate offer independent of the service catalog — if the client
// ever wants to change the price or duration, it's a one-line edit here
// rather than a data migration, and both apps/web and apps/api read the
// same value so a UI display and the server-computed charge can never
// drift apart.
export const CONSULTATION_FEE_INR = 499;
export const CONSULTATION_DURATION_MINUTES = 30;
